import React, { useState, useEffect, useCallback } from 'react';
import { Sidebar } from '../includes/sidebar';
import { Topbar } from '../includes/topbar';
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from '../../components/ui/breadcrumb';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import {
    CheckCircle2,
    XCircle,
    Clock,
    Loader2,
    User,
    CreditCard,
    RefreshCw,
} from 'lucide-react';
import { transactionsApi, WithdrawalRequestModel, WithdrawalRequestStatus } from '../../api/transactions.api';
import { useToast } from '../../hooks/use-toast';

const formatHTG = (amount: string | number) =>
    new Intl.NumberFormat('fr-HT', { style: 'currency', currency: 'HTG' }).format(Number(amount));

const statusConfig: Record<WithdrawalRequestStatus, { label: string; className: string }> = {
    pending: { label: 'An Atant', className: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
    approved: { label: 'Apwouve', className: 'bg-green-50 text-green-700 border-green-200' },
    rejected: { label: 'Rejte', className: 'bg-red-50 text-red-700 border-red-200' },
};

export default function WithdrawalRequestsPage() {
    const { toast } = useToast();
    const [requests, setRequests] = useState<WithdrawalRequestModel[]>([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState<WithdrawalRequestStatus>('pending');

    const [selectedRequest, setSelectedRequest] = useState<WithdrawalRequestModel | null>(null);
    const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);
    const [rejectionReason, setRejectionReason] = useState('');
    const [processing, setProcessing] = useState(false);

    const [backendUnavailable, setBackendUnavailable] = useState(false);

    const loadRequests = useCallback(async () => {
        try {
            setLoading(true);
            setBackendUnavailable(false);
            const response = await transactionsApi.getWithdrawalRequests({ status: statusFilter });
            setRequests(response.requests || []);
        } catch (err: any) {
            const status = err?.response?.status;
            if (status === 404) {
                // Endpoint poko deploye sou servè sa a
                setBackendUnavailable(true);
            } else {
                toast({ title: 'Erè', description: 'Pa kapab chaje demann retrè yo', type: 'error' });
            }
        } finally {
            setLoading(false);
        }
    }, [statusFilter, toast]);

    useEffect(() => {
        loadRequests();
    }, [loadRequests]);

    const openAction = (req: WithdrawalRequestModel, type: 'approve' | 'reject') => {
        setSelectedRequest(req);
        setActionType(type);
        setRejectionReason('');
    };

    const closeModal = () => {
        setSelectedRequest(null);
        setActionType(null);
        setRejectionReason('');
    };

    const handleConfirmAction = async () => {
        if (!selectedRequest || !actionType) return;
        setProcessing(true);
        try {
            if (actionType === 'approve') {
                await transactionsApi.approveWithdrawalRequest(selectedRequest.id);
                toast({ title: 'Siksè', description: 'Demann retrè apwouve', type: 'success' });
            } else {
                await transactionsApi.rejectWithdrawalRequest(selectedRequest.id, rejectionReason || undefined);
                toast({ title: 'Siksè', description: 'Demann retrè rejte', type: 'success' });
            }
            closeModal();
            loadRequests();
        } catch (err: any) {
            const msg = err?.response?.data?.error || err?.response?.data?.message || 'Erè sèvè';
            toast({ title: 'Erè', description: msg, type: 'error' });
        } finally {
            setProcessing(false);
        }
    };

    return (
        <div className="flex h-screen overflow-hidden bg-slate-50">
            <Sidebar />
            <div className="flex-1 flex flex-col overflow-hidden">
                <Topbar />
                <main className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6">
                    {/* Breadcrumb */}
                    <Breadcrumb>
                        <BreadcrumbList>
                            <BreadcrumbItem>
                                <BreadcrumbLink href="/dashboard">Dashboard</BreadcrumbLink>
                            </BreadcrumbItem>
                            <BreadcrumbSeparator />
                            <BreadcrumbItem>
                                <BreadcrumbLink href="/transactions">Tranzaksyon</BreadcrumbLink>
                            </BreadcrumbItem>
                            <BreadcrumbSeparator />
                            <BreadcrumbItem>
                                <BreadcrumbPage>Demann Retrè</BreadcrumbPage>
                            </BreadcrumbItem>
                        </BreadcrumbList>
                    </Breadcrumb>

                    {/* Header */}
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900">Demann Retrè Ajan</h1>
                            <p className="text-sm text-slate-500 mt-1">Jere demann retrè ki bezwen apwobasyon admin</p>
                        </div>
                        <Button variant="outline" size="sm" onClick={loadRequests} disabled={loading}>
                            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                            Aktualize
                        </Button>
                    </div>

                    {/* Filter tabs */}
                    <div className="flex gap-2 border-b">
                        {(['pending', 'approved', 'rejected'] as WithdrawalRequestStatus[]).map(s => (
                            <button
                                key={s}
                                onClick={() => setStatusFilter(s)}
                                className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
                                    statusFilter === s
                                        ? 'border-blue-600 text-blue-600'
                                        : 'border-transparent text-slate-500 hover:text-slate-700'
                                }`}
                            >
                                {statusConfig[s].label}
                            </button>
                        ))}
                    </div>

                    {/* Content */}
                    <Card>
                        <CardContent className="p-0">
                            {loading ? (
                                <div className="flex justify-center items-center py-16">
                                    <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                                </div>
                            ) : backendUnavailable ? (
                                <div className="flex flex-col items-center justify-center py-16 text-slate-400 gap-2">
                                    <Clock className="h-12 w-12 opacity-20" />
                                    <p className="text-sm font-medium text-slate-500">Fonksyonalite sa poko disponib</p>
                                    <p className="text-xs text-slate-400">Backend poko deploye — ap vini byento</p>
                                </div>
                            ) : requests.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                                    <Clock className="h-12 w-12 mb-3 opacity-30" />
                                    <p className="text-sm">Pa gen demann {statusConfig[statusFilter].label.toLowerCase()}</p>
                                </div>
                            ) : (
                                <div className="divide-y">
                                    {requests.map(req => (
                                        <div key={req.id} className="p-4 flex items-start justify-between gap-4 hover:bg-slate-50 transition-colors">
                                            <div className="space-y-1 flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="font-semibold text-slate-800 text-sm">{req.client_name}</span>
                                                    <Badge variant="outline" className={`text-xs ${statusConfig[req.status].className}`}>
                                                        {statusConfig[req.status].label}
                                                    </Badge>
                                                </div>
                                                <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                                                    <span className="flex items-center gap-1">
                                                        <CreditCard className="h-3 w-3" />
                                                        {req.compte_code}
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <User className="h-3 w-3" />
                                                        {req.agent_name || req.agent_id.slice(0, 8) + '…'}
                                                    </span>
                                                    <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-medium">
                                                        Retrè BS
                                                    </span>
                                                    <span>{new Date(req.initiated_at).toLocaleString('fr-HT')}</span>
                                                </div>
                                                {req.rejection_reason && (
                                                    <p className="text-xs text-red-500 mt-1">Rezon: {req.rejection_reason}</p>
                                                )}
                                            </div>
                                            <div className="flex flex-col items-end gap-2 shrink-0">
                                                <span className="text-base font-bold text-slate-800">
                                                    {formatHTG(req.montant)}
                                                </span>
                                                {req.status === 'pending' && (
                                                    <div className="flex gap-2">
                                                        <Button
                                                            size="sm"
                                                            className="bg-green-600 hover:bg-green-700 text-white h-7 px-3 text-xs"
                                                            onClick={() => openAction(req, 'approve')}
                                                        >
                                                            <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                                                            Apwouve
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            className="border-red-300 text-red-600 hover:bg-red-50 h-7 px-3 text-xs"
                                                            onClick={() => openAction(req, 'reject')}
                                                        >
                                                            <XCircle className="h-3.5 w-3.5 mr-1" />
                                                            Rejte
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </main>
            </div>

            {/* Action Modal */}
            <Dialog open={!!selectedRequest} onOpenChange={open => { if (!open) closeModal(); }}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>
                            {actionType === 'approve' ? 'Konfime Apwobasyon' : 'Konfime Rejeksyon'}
                        </DialogTitle>
                        <DialogDescription>
                            {selectedRequest && (
                                <>
                                    <strong>{selectedRequest.client_name}</strong> — {formatHTG(selectedRequest.montant)}
                                    <br />
                                    Kont: {selectedRequest.compte_code}
                                    {selectedRequest.agent_name && (
                                        <> · Ajan: {selectedRequest.agent_name}</>
                                    )}
                                </>
                            )}
                        </DialogDescription>
                    </DialogHeader>

                    {actionType === 'reject' && (
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-700">Rezon rejeksyon (opsyonèl)</label>
                            <Textarea
                                placeholder="Bay rezon rejet la..."
                                value={rejectionReason}
                                onChange={e => setRejectionReason(e.target.value)}
                                rows={3}
                            />
                        </div>
                    )}

                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={closeModal} disabled={processing}>
                            Anile
                        </Button>
                        <Button
                            onClick={handleConfirmAction}
                            disabled={processing}
                            className={actionType === 'approve'
                                ? 'bg-green-600 hover:bg-green-700'
                                : 'bg-red-600 hover:bg-red-700'
                            }
                        >
                            {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            {actionType === 'approve' ? 'Wi, Apwouve' : 'Wi, Rejte'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
